import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarX, Loader2, Save } from 'lucide-react';
import {
  DEFAULT_SCHEDULE,
  Job,
  SCHEDULE_KIND_OPTIONS,
  Schedule,
  ScheduleKind,
  WEEKDAYS,
  Weekday,
  browserTimeZone,
  sameSchedule,
  timeZoneOptions,
} from '../../services/JobMonitorService';
import { ConfirmDialog } from '../../components/library/modals/ConfirmDialog';
import { formatTimestamp } from './formatters';
import styles from '../JobsPage.module.css';

interface ScheduleEditorProps {
  job: Job;
  onSave: (schedule: Schedule, enabled: boolean) => Promise<void>;
  onClear: () => Promise<void>;
}

const INTERVAL_LABEL: Partial<Record<ScheduleKind, string>> = {
  every_minutes: 'Minutes between runs',
  every_hours: 'Hours between runs',
  every_days: 'Days between runs',
  windowed: 'Minutes between runs inside the window',
};

// Kinds that fire at a wall-clock time, so the day and the timezone matter.
const CLOCK_KINDS = new Set<ScheduleKind>(['weekly', 'windowed']);
const ALL_DAYS: Weekday[] = WEEKDAYS.map((day) => day.value);

export const ScheduleEditor: React.FC<ScheduleEditorProps> = ({ job, onSave, onClear }) => {
  const serverSchedule = job.schedule ?? DEFAULT_SCHEDULE;
  const [draft, setDraft] = useState<Schedule>(serverSchedule);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastSynced = useRef<Schedule>(serverSchedule);
  const zones = useMemo(() => timeZoneOptions(), []);

  // The jobs list is re-fetched on a timer, so this prop is a new object on every
  // poll; only adopt it when the schedule itself changed, never on identity churn.
  useEffect(() => {
    if (sameSchedule(lastSynced.current, serverSchedule)) return;
    lastSynced.current = serverSchedule;
    setDraft(serverSchedule);
  }, [serverSchedule]);

  const patch = (next: Partial<Schedule>) => setDraft((current) => ({ ...current, ...next }));

  const changeKind = (kind: ScheduleKind) => {
    if (kind === 'manual') {
      setDraft({ ...DEFAULT_SCHEDULE });
      return;
    }

    if (!CLOCK_KINDS.has(kind)) {
      patch({ kind, interval: draft.interval ?? 15, days_of_week: [], time_of_day: null, end_time_of_day: null, timezone: 'UTC' });
      return;
    }

    patch({
      kind,
      interval: kind === 'windowed' ? draft.interval ?? 30 : null,
      days_of_week: draft.days_of_week.length ? draft.days_of_week : ALL_DAYS,
      time_of_day: draft.time_of_day ?? '09:00',
      end_time_of_day: kind === 'windowed' ? draft.end_time_of_day ?? '16:00' : null,
      timezone: CLOCK_KINDS.has(draft.kind) ? draft.timezone : browserTimeZone(),
    });
  };

  const toggleDay = (day: Weekday) => {
    const selected = draft.days_of_week.includes(day)
      ? draft.days_of_week.filter((value) => value !== day)
      : [...draft.days_of_week, day];
    patch({ days_of_week: selected });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(draft, draft.kind !== 'manual');
    } catch (e) {
      setError((e as Error).message || 'Could not save the schedule');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setConfirmingClear(false);
    setClearing(true);
    setError(null);
    try {
      await onClear();
      setDraft({ ...DEFAULT_SCHEDULE });
    } catch (e) {
      setError((e as Error).message || 'Could not clear the schedule');
    } finally {
      setClearing(false);
    }
  };

  const busy = saving || clearing;

  if (!job.schedulable) {
    return (
      <div>
        <h4 className={styles.sectionTitle}>Schedule</h4>
        <p className={styles.inlineNote}>This job is manual only and has no timer unit.</p>
      </div>
    );
  }

  const intervalLabel = INTERVAL_LABEL[draft.kind];
  const onAClock = CLOCK_KINDS.has(draft.kind);

  return (
    <div>
      <h4 className={styles.sectionTitle}>Schedule</h4>

      <div className={styles.field}>
        <label className={styles.label} htmlFor={`kind-${job.job_id}`}>Run this job</label>
        <select
          id={`kind-${job.job_id}`}
          className={styles.select}
          value={draft.kind}
          disabled={busy}
          onChange={(e) => changeKind(e.target.value as ScheduleKind)}
        >
          {SCHEDULE_KIND_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      {intervalLabel && (
        <div className={styles.field}>
          <label className={styles.label} htmlFor={`interval-${job.job_id}`}>{intervalLabel}</label>
          <input
            id={`interval-${job.job_id}`}
            className={styles.input}
            type="number"
            min={1}
            value={draft.interval ?? 1}
            disabled={busy}
            onChange={(e) => patch({ interval: Number(e.target.value) })}
          />
        </div>
      )}

      {onAClock && (
        <>
          <div className={styles.field}>
            <span className={styles.label}>Days of the week</span>
            <div className={styles.dayRow}>
              {WEEKDAYS.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  disabled={busy}
                  className={`${styles.dayToggle} ${draft.days_of_week.includes(day.value) ? styles.dayToggleActive : ''}`}
                  onClick={() => toggleDay(day.value)}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={`time-${job.job_id}`}>
              {draft.kind === 'windowed' ? 'Start at' : 'Time of day'}
            </label>
            <input
              id={`time-${job.job_id}`}
              className={styles.input}
              type="time"
              value={draft.time_of_day ?? '09:00'}
              disabled={busy}
              onChange={(e) => patch({ time_of_day: e.target.value })}
            />
          </div>
          {draft.kind === 'windowed' && (
            <div className={styles.field}>
              <label className={styles.label} htmlFor={`end-time-${job.job_id}`}>Repeat until</label>
              <input
                id={`end-time-${job.job_id}`}
                className={styles.input}
                type="time"
                value={draft.end_time_of_day ?? '16:00'}
                disabled={busy}
                onChange={(e) => patch({ end_time_of_day: e.target.value })}
              />
            </div>
          )}
          <div className={styles.field}>
            <label className={styles.label} htmlFor={`timezone-${job.job_id}`}>Timezone</label>
            <select
              id={`timezone-${job.job_id}`}
              className={styles.select}
              value={draft.timezone}
              disabled={busy}
              onChange={(e) => patch({ timezone: e.target.value })}
            >
              {zones.map((zone) => (
                <option key={zone} value={zone}>{zone}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {error && <p className={styles.errorBar}>{error}</p>}

      <div className={styles.actions}>
        <button type="button" className={styles.primaryBtn} onClick={save} disabled={busy}>
          {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />}
          {saving ? 'Saving…' : 'Save schedule'}
        </button>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={() => setConfirmingClear(true)}
          disabled={busy || !job.schedule_enabled}
        >
          {clearing ? <Loader2 size={14} className={styles.spin} /> : <CalendarX size={14} />}
          {clearing ? 'Clearing…' : 'Clear schedule'}
        </button>
      </div>

      <p className={styles.inlineNote}>
        Current: {job.schedule_summary}
        {job.next_run_at ? ` · next run ${formatTimestamp(job.next_run_at)}` : ''}
      </p>

      {confirmingClear && (
        <ConfirmDialog
          title="Clear schedule"
          message={`"${job.display_name}" will no longer run on a timer. It will only run when started by hand.`}
          confirmLabel="Clear schedule"
          variant="danger"
          onConfirm={clear}
          onCancel={() => setConfirmingClear(false)}
        />
      )}
    </div>
  );
};
